import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authService, resolveAuthLoginUrl } from '../services/api';

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    async function fetchUser() {
      try {
        const response = await authService.getCurrentUser();
        if (!isMounted) {
          return;
        }
        setUser(response.data.user);
        setError(null);
      } catch (err) {
        if (!isMounted) {
          return;
        }
        setUser(null);
        if (err?.response && err.response.status === 401) {
          setError(null);
        } else {
          setError('Unable to verify session.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    fetchUser();

    return () => {
      isMounted = false;
    };
  }, []);

  const loginWithGoogle = useCallback((redirectPath) => {
    const authUrl = resolveAuthLoginUrl(redirectPath);
    // Force full page navigation to backend (not React Router navigation)
    window.location.replace(authUrl);
  }, []);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      setUser(null);
    }
  }, []);

  const value = useMemo(() => ({
    user,
    loading,
    error,
    loginWithGoogle,
    logout,
    setUser,
  }), [user, loading, error, loginWithGoogle, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};
